import React from 'react'
import { Employee } from '@/types/workforce'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import {
  Mail,
  Phone,
  Building2,
  Briefcase,
  Calendar,
  UserCircle,
  ArrowRight
} from 'lucide-react'

interface DirectReportsCardProps {
  reports: Employee[]
  onViewDetails: (employee: Employee) => void
  onReassign?: (employee: Employee) => void
  loading?: boolean
}

export function DirectReportsCard({
  reports,
  onViewDetails,
  onReassign,
  loading = false
}: DirectReportsCardProps) {
  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-success/20 text-success'
      case 'INACTIVE':
        return 'bg-brand-gray/20 text-brand-gray'
      case 'TERMINATED':
        return 'bg-error/20 text-error'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'Ativo'
      case 'INACTIVE':
        return 'Inativo'
      case 'TERMINATED':
        return 'Demitido'
      case 'PENDING':
        return 'Pendente'
      default:
        return status
    }
  }

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(date))
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="p-6 animate-pulse">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-brand-gray/20 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-brand-gray/20 rounded w-1/3"></div>
                <div className="h-3 bg-brand-gray/20 rounded w-1/2"></div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    )
  }

  if (reports.length === 0) {
    return (
      <Card className="p-12 text-center">
        <UserCircle className="w-16 h-16 mx-auto mb-4 text-brand-gray" />
        <h3 className="font-display text-lg font-semibold text-white mb-2">
          Nenhum Subordinado Direto
        </h3>
        <p className="text-brand-gray font-body">
          Você não possui subordinados diretos no momento.
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {reports.map((employee) => {
        const name = employee.personalInfo?.name || 'Nome não informado'
        const nameParts = name.split(' ')
        const initials =
          nameParts.length >= 2
            ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
            : `${nameParts[0][0]}${nameParts[0][1] || ''}`

        return (
          <Card key={employee.employeeId} className="card-hover p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                {/* Avatar */}
                <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-monumenta group-hover:shadow-monumenta-brand transition-all duration-200">
                  <span className="text-sm font-bold text-brand-dark font-display">
                    {initials.toUpperCase()}
                  </span>
                </div>

                {/* Employee Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3
                      className="font-display text-lg font-semibold text-white hover:text-brand-primary transition-colors cursor-pointer"
                      onClick={() => onViewDetails(employee)}
                    >
                      {name}
                    </h3>
                    <Badge className={getStatusColor(employee.employment.status)}>
                      {getStatusLabel(employee.employment.status)}
                    </Badge>
                  </div>

                  {/* Contact & Role Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-brand-gray">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{employee.email || 'N/A'}</span>
                    </div>
                    {employee.personalInfo.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{employee.personalInfo.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">
                        {employee.employment.department?.name || 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">
                        {employee.employment.role?.name || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="flex items-center gap-4 mt-3 text-sm text-brand-gray">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Admissão: {formatDate(employee.employment.startDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewDetails(employee)}
                  className="group"
                >
                  Ver Detalhes
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </Card>
        )
      })}
    </div>
  )
}
